import {useState} from 'react'

import {noop} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {FormikErrors, useFormik} from 'formik'
import invariant from 'invariant'
import {useLocation, useNavigate} from 'react-router'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import * as validator from '../../../../commons/src/libs/validator'
import * as authService from '../../../../commons/src/services/auth'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import {FromLocationState, goToSignIn} from '../../services/navigation'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'
import emailIcon from '../../../../commons/src/assets/icons/email.svg'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import List from '../../../../commons/src/components/list'
import Paragraph from '../../../../commons/src/components/paragraph'
import PasswordInputField from '../../../../commons/src/components/password-input-field'
import SuccessMessage from '../../../../commons/src/components/success-message'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'

import AuthLayoutPartial from './auth-layout-partial'

type StoredData = {
  email: string
  password?: string
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as undefined | FromLocationState

  const [storedData, setStoredData] = useState<StoredData | undefined>()
  const [mailResending, setMailResending] = useState(false)
  const [isDone, setIsDone] = useState(false)

  const formikForgotPassword = useFormik({
    initialValues: {
      email: '',
      password: '',
      // Additional errors
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.combineValidators(validator.required, validator.email)(values.email)) {
        errors.email = 'true'
      }

      return errors
    },
    onSubmit: (values, actions) => {
      const email = values.email

      return authService
        .forgotPassword(email)
        .then(() => {
          setStoredData({email})
        })
        .catch(error => actions.setFieldError('general', error.message))
    },
  })

  const formikConfirm = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      authCode: '',
      // Additional errors
      general: false,
    },
    validate: values => {
      const errors: Record<string, boolean> = {}

      if (!validator.required(values.authCode)) {
        errors.authCode = true
      }

      if (!validator.required(values.password)) {
        errors.password = true
      }

      if (!validator.required(values.confirmPassword)) {
        errors.confirmPassword = true
      }

      return errors
    },
    onSubmit: (values, actions) => {
      if (values.password !== values.confirmPassword) {
        actions.setFieldError('general', 'Please make sure the provided passwords are equal.')
        actions.setFieldError('password', 'true')
        actions.setFieldError('confirmPassword', 'true')
        return Promise.resolve()
      }

      invariant(storedData, 'storedData is missing in order to submit forgot password')

      return authService
        .setNewPassword(storedData.email, values.authCode, values.password)
        .then(() => {
          setStoredData({...storedData, password: values.password})
          setIsDone(true)
        })
        .catch(error => {
          if (
            (error.name === 'InvalidParameterException' ||
              error.name === 'InvalidPasswordException') &&
            error.message.match(/password/gi)
          ) {
            actions.setFieldError('password', 'true')
            actions.setFieldError('confirmPassword', 'true')
            actions.setFieldError(
              'general',
              'Your password must have a minimum length of 8 characters and contain a combination of upper-case, lower-case and digits.'
            )
          } else {
            actions.setFieldError('general', error.message)
          }
        })
    },
  })

  useKeyPress(
    preventDoubleClick(() => {
      if (!isDone && !storedData) {
        formikForgotPassword.handleSubmit()
      } else if (!isDone && storedData) {
        formikConfirm.handleSubmit()
      }
    }),
    'Enter'
  )

  return (
    <AuthLayoutPartial>
      <Headline label="Account Recovery" className="u-align-center" />
      {!isDone && !storedData && (
        <form onSubmit={formikForgotPassword.handleSubmit}>
          {!formikForgotPassword.errors.general && (
            <Paragraph>
              Please enter the E-Mail address of your Craftcloud® Partner Portal account.
            </Paragraph>
          )}
          {formikForgotPassword.errors.general && (
            <Paragraph error>{formikForgotPassword.errors.general}</Paragraph>
          )}
          <FormRow>
            <InputField
              name="email"
              placeholder="E-Mail"
              size="200"
              icon={<Icon source={emailIcon} />}
              type="email"
              autoFocus
              appearance={
                formikForgotPassword.submitCount > 0 && !!formikForgotPassword.errors.email
                  ? 'error'
                  : undefined
              }
              onChange={(v, n, event) => formikForgotPassword.handleChange(event)}
              value={formikForgotPassword.values.email}
              onBlur={formikForgotPassword.handleBlur}
              disabled={formikForgotPassword.isSubmitting}
            />
          </FormRow>
          <Button
            block
            disabled={formikForgotPassword.isSubmitting || !isEmpty(formikForgotPassword.errors)}
            onClick={preventDoubleClick(() => formikForgotPassword.handleSubmit())}
          >
            Continue
          </Button>
        </form>
      )}
      {!isDone && storedData && (
        <form onSubmit={formikConfirm.handleSubmit}>
          {!formikConfirm.errors.general && (
            <Paragraph>Please enter the verification code you received by E-Mail.</Paragraph>
          )}
          {formikConfirm.errors.general && (
            <Paragraph error>{formikConfirm.errors.general}</Paragraph>
          )}
          <FormRow>
            <InputField
              name="authCode"
              placeholder="Verification code"
              autoComplete="off"
              appearance={
                formikConfirm.submitCount > 0 && !!formikConfirm.errors.authCode
                  ? 'error'
                  : undefined
              }
              onChange={(v, n, event) => formikConfirm.handleChange(event)}
              value={formikConfirm.values.authCode}
              onBlur={formikConfirm.handleBlur}
              disabled={formikConfirm.isSubmitting}
            />
          </FormRow>
          <Paragraph minor size="s">
            Please check your spam folder if you have not received the E-Mail.{' '}
            <Link
              onClick={
                mailResending
                  ? noop
                  : async () => {
                      setMailResending(true)
                      await authService
                        .forgotPassword(storedData.email)
                        .catch(error => formikConfirm.setFieldError('general', error.message))
                      setMailResending(false)
                    }
              }
            >
              {mailResending ? 'Resending…' : ' Resend E-Mail'}
            </Link>
          </Paragraph>
          <FormRow>
            <FieldNote
              note={
                <TooltipHandler
                  openOnClick
                  tooltip={
                    <Tooltip
                      footer={
                        <List>
                          <li>
                            have a minimum <strong>length of 8 characters</strong>
                          </li>
                          <li>
                            contain at least <strong>one digit</strong>
                          </li>
                          <li>
                            contain at least <strong>one upper-case character</strong>
                          </li>
                          <li>
                            contain at least <strong>one lower-case character</strong>
                          </li>
                        </List>
                      }
                    >
                      Your password must…
                    </Tooltip>
                  }
                >
                  <LinkButton>Show password requirements</LinkButton>
                </TooltipHandler>
              }
            >
              <PasswordInputField
                name="password"
                placeholder="New password"
                autoComplete="new-password"
                appearance={
                  formikConfirm.submitCount > 0 && !!formikConfirm.errors.password
                    ? 'error'
                    : undefined
                }
                onChange={(v, n, event) => formikConfirm.handleChange(event)}
                value={formikConfirm.values.password}
                onBlur={formikConfirm.handleBlur}
                disabled={formikConfirm.isSubmitting}
              />
            </FieldNote>
          </FormRow>
          <FormRow>
            <PasswordInputField
              name="confirmPassword"
              placeholder="Repeat password"
              autoComplete="off"
              appearance={
                formikConfirm.submitCount > 0 && !!formikConfirm.errors.confirmPassword
                  ? 'error'
                  : undefined
              }
              onChange={(v, n, event) => formikConfirm.handleChange(event)}
              value={formikConfirm.values.confirmPassword}
              onBlur={formikConfirm.handleBlur}
              disabled={formikConfirm.isSubmitting}
            />
          </FormRow>
          <Button
            block
            disabled={
              formikConfirm.isSubmitting ||
              (formikConfirm.submitCount > 0 && !isEmpty(formikConfirm.errors))
            }
            onClick={preventDoubleClick(() => formikConfirm.handleSubmit())}
          >
            Continue
          </Button>
        </form>
      )}
      {isDone && (
        <>
          <SuccessMessage>You have successfully updated your password!</SuccessMessage>
          <Button
            block
            onClick={preventDoubleClick(() => {
              invariant(storedData, 'storedData is missing in order to sign in')
              goToSignIn(navigate, {
                locationState: undefined,
                replace: true,
              })
            })}
          >
            Continue
          </Button>
        </>
      )}
      {!isDone && (
        <LinkButton
          className="u-margin-top"
          onClick={() => goToSignIn(navigate, {locationState, replace: true})}
          icon={<Icon source={chevronLeftIcon} />}
        >
          Back to sign in
        </LinkButton>
      )}
    </AuthLayoutPartial>
  )
}

export default ResetPasswordPage
